import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { collection, query, where, onSnapshot, getDocs, updateDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../../config/firebaseConfig';
import { displayNameOf, resolveUserIdentity } from '../../utils/userIdentity';

/**
 * Uygulama geneli "gelen mesaj" bildirimi.
 *
 * Gelen aramalar (bkz. VideoCallContext) kullanıcı hangi sayfada olursa olsun
 * tam ekran bir modalla gösteriliyor. Mesajlar için aynı yaklaşım fazla
 * rahatsız edici olurdu: dinleyici burada da uygulama genelinde çalışır, ancak
 * sonuç köşede kendiliğinden kaybolan sade bir kart olarak gösterilir.
 */

const MessageNotificationContext = createContext({
  notifications: [],
  unreadCount: 0,
  unreadBySender: {},
  dismissNotification: () => {},
  setActiveChatEmail: () => {},
  markSenderRead: () => {},
});

export const useMessageNotifications = () => useContext(MessageNotificationContext);

/** Uygulama kapalıyken birikmiş mesajlar açılışta bildirim üretmesin. */
const NOTIFY_TTL_MS = 2 * 60 * 1000;
/** Kart bu süre sonunda kendiliğinden kapanır. */
const AUTO_DISMISS_MS = 7000;
/** Aynı anda ekranda tutulacak en fazla kart sayısı. */
const MAX_VISIBLE = 3;

/** Mesaj belgesinin zaman damgasını milisaniyeye çevirir. */
const toMillis = (timestamp) => {
  if (!timestamp) return null;
  if (typeof timestamp.toMillis === 'function') return timestamp.toMillis();
  if (typeof timestamp.seconds === 'number') return timestamp.seconds * 1000;
  return null;
};

/** Kartta ve sistem bildiriminde gösterilecek kısa önizleme. */
const previewOf = (message) => {
  const text = (message.text || '').trim();
  if (text) return text.length > 90 ? `${text.slice(0, 90)}…` : text;
  if (message.fileName) return `📎 ${message.fileName}`;
  if (message.fileUrl) return '📎 Dosya gönderdi';
  return 'Yeni mesaj';
};

/**
 * Kısa ve alçak sesli bir "ding". Arama zilinin aksine tekrarlamaz; tek
 * seferlik iki nota çalar, böylece kullanıcı fark eder ama işi bölünmez.
 */
const playChime = () => {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});

    [[880, 0], [1174.7, 0.09]].forEach(([frequency, offset]) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = frequency;
      osc.connect(gain);
      gain.connect(ctx.destination);

      const t = ctx.currentTime + offset;
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.07, t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.28);
      osc.start(t);
      osc.stop(t + 0.3);
    });

    // Her bildirimde yeni bir AudioContext açılıyor; osilatörler bittikten
    // sonra kapatılmazsa tarayıcının bağlam limiti doluyor.
    setTimeout(() => ctx.close().catch(() => {}), 600);
  } catch (e) {
    // Ses çalınamazsa görsel kart tek başına yeterli
  }
};

export const MessageNotificationProvider = ({ children }) => {
  const [authEmail, setAuthEmail] = useState(auth.currentUser?.email || null);
  const [notifications, setNotifications] = useState([]);
  const [unreadBySender, setUnreadBySender] = useState({});

  // Halihazırda açık olan sohbet: o kişiden gelen mesaj için kart çıkarmayız,
  // kullanıcı mesajı zaten ekranda görüyor.
  const activeChatRef = useRef(null);
  // İlk snapshot geçmiş okunmamışları taşır; bunlar için bildirim üretilmez.
  const primedRef = useRef(false);
  // Aynı mesaj için ikinci kez kart çıkmasın (snapshot'lar tekrar gelebiliyor).
  const notifiedIdsRef = useRef(new Set());
  // E-posta -> görünen ad. Her mesajda Users sorgusu atmamak için.
  const nameCacheRef = useRef(new Map());

  // Oturum, sağlayıcı mount olduktan sonra geri yüklenir; auth.currentUser'ı
  // doğrudan okumak yerine onAuthStateChanged ile takip ediyoruz. Aynı sorun
  // gelen arama dinleyicisinde de yaşanmıştı.
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setAuthEmail(user?.email || null);
      // Kullanıcı değişti: birikmiş durumu sıfırla
      primedRef.current = false;
      notifiedIdsRef.current = new Set();
      setNotifications([]);
      setUnreadBySender({});
    });
    return () => unsubscribe();
  }, []);

  const setActiveChatEmail = useCallback((email) => {
    activeChatRef.current = email || null;
    if (email) {
      // Sohbet açıldıysa o kişiye ait bekleyen kartları da kaldır
      setNotifications((prev) => prev.filter((n) => n.senderEmail !== email));
    }
  }, []);

  const dismissNotification = useCallback((id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  /**
   * Gönderenin ad soyadını çözer (önce Employees, sonra Users) ve önbelleğe
   * alır. Kayıt bulunamazsa e-postadan okunabilir bir ad üretilir; bildirimde
   * artık ham e-posta gösterilmez.
   */
  const resolveSenderName = useCallback(async (email) => {
    if (!email) return 'Bilinmeyen kullanıcı';
    if (nameCacheRef.current.has(email)) return nameCacheRef.current.get(email);

    const identity = await resolveUserIdentity(email);
    const name = displayNameOf(identity);
    if (identity.image) {
      nameCacheRef.current.set(`${email}::image`, identity.image);
    }
    nameCacheRef.current.set(email, name);
    return name;
  }, []);

  // Uygulama geneli okunmamış mesaj dinleyicisi.
  // Yalnızca eşitlik filtreleri kullanılır (orderBy yok) ki bileşik dizin
  // gerekmesin; sıralama ve süzme istemci tarafında yapılır.
  useEffect(() => {
    if (!authEmail) return undefined;

    primedRef.current = false;

    const q = query(
      collection(db, 'messages'),
      where('receiverEmail', '==', authEmail),
      where('read', '==', false)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      // Okunmamış sayaçları her snapshot'ta yeniden hesapla
      const counts = {};
      snapshot.docs.forEach((d) => {
        const sender = d.data().senderEmail;
        if (!sender) return;
        counts[sender] = (counts[sender] || 0) + 1;
      });
      setUnreadBySender(counts);

      // İlk snapshot geçmişi taşır: bildirim üretmeden yalnızca işaretle
      if (!primedRef.current) {
        primedRef.current = true;
        snapshot.docs.forEach((d) => notifiedIdsRef.current.add(d.id));
        return;
      }

      const now = Date.now();
      const arrivals = [];

      snapshot.docChanges().forEach((change) => {
        if (change.type !== 'added') return;
        if (notifiedIdsRef.current.has(change.doc.id)) return;
        notifiedIdsRef.current.add(change.doc.id);

        const data = change.doc.data();

        // Arama davetleri zaten IncomingCallModal ile gösteriliyor
        if (data.callRoomId || data.type === 'call-invitation') return;
        // Sohbet zaten açıksa ve sekme öndeyse kart gösterme
        if (!document.hidden && activeChatRef.current === data.senderEmail) return;

        // serverTimestamp() ilk yazımda null gelir; damgası olmayan belgeyi
        // yeni kabul ediyoruz, TTL yalnızca damgası olanlara uygulanır.
        const sentAt = toMillis(data.timestamp);
        if (sentAt && now - sentAt > NOTIFY_TTL_MS) return;

        arrivals.push({ id: change.doc.id, ...data });
      });

      // Okunan mesajlar sorgudan düşse de kimlikleri sette kalıyor. Uzun
      // oturumlarda sınırsız büyümesin diye, bu turdaki eklemeler işlendikten
      // sonra seti yalnızca hâlâ okunmamış olanlara daraltıyoruz.
      if (notifiedIdsRef.current.size > 1000) {
        notifiedIdsRef.current = new Set(snapshot.docs.map((d) => d.id));
      }

      if (arrivals.length === 0) return;

      playChime();

      arrivals.forEach(async (message) => {
        const senderName = await resolveSenderName(message.senderEmail);
        const senderImage = nameCacheRef.current.get(`${message.senderEmail}::image`) || null;

        setNotifications((prev) => [
          ...prev.filter((n) => n.id !== message.id),
          {
            id: message.id,
            senderEmail: message.senderEmail,
            senderName,
            senderImage,
            preview: previewOf(message),
            isBroadcast: !!message.isBroadcast,
            receivedAt: Date.now(),
          },
        ].slice(-MAX_VISIBLE));

        // Sekme arka plandaysa sistem bildirimi de göster (izin verilmişse)
        try {
          if (typeof Notification !== 'undefined' &&
              document.hidden &&
              Notification.permission === 'granted') {
            const systemNotification = new Notification(senderName, {
              body: previewOf(message),
              tag: `habcare-message-${message.senderEmail}`,
              icon: senderImage || undefined,
            });
            systemNotification.onclick = () => {
              window.focus();
              systemNotification.close();
            };
          }
        } catch (e) {
          // Bildirim desteklenmiyorsa uygulama içi kart yeterli
        }
      });
    }, (error) => {
      console.error('Mesaj bildirimi dinleyicisi hatası:', error);
    });

    return () => unsubscribe();
  }, [authEmail, resolveSenderName]);

  // Kartlar süresi dolunca kendiliğinden kapanır
  useEffect(() => {
    if (notifications.length === 0) return undefined;
    const timers = notifications.map((n) =>
      setTimeout(
        () => dismissNotification(n.id),
        Math.max(1000, AUTO_DISMISS_MS - (Date.now() - n.receivedAt))
      )
    );
    return () => timers.forEach(clearTimeout);
  }, [notifications, dismissNotification]);

  /** Bir kişiyle olan okunmamış mesajların tümünü okundu işaretler. */
  const markSenderRead = useCallback(async (senderEmail) => {
    if (!authEmail || !senderEmail) return;
    setNotifications((prev) => prev.filter((n) => n.senderEmail !== senderEmail));
    try {
      const snapshot = await getDocs(query(
        collection(db, 'messages'),
        where('receiverEmail', '==', authEmail),
        where('senderEmail', '==', senderEmail),
        where('read', '==', false)
      ));
      await Promise.all(snapshot.docs.map((d) => updateDoc(d.ref, { read: true })));
    } catch (error) {
      console.error('Mesajlar okundu işaretlenemedi:', error);
    }
  }, [authEmail]);

  const unreadCount = useMemo(
    () => Object.values(unreadBySender).reduce((sum, n) => sum + n, 0),
    [unreadBySender]
  );

  const value = useMemo(() => ({
    notifications,
    unreadCount,
    unreadBySender,
    dismissNotification,
    setActiveChatEmail,
    markSenderRead,
  }), [
    notifications,
    unreadCount,
    unreadBySender,
    dismissNotification,
    setActiveChatEmail,
    markSenderRead,
  ]);

  return (
    <MessageNotificationContext.Provider value={value}>
      {children}
    </MessageNotificationContext.Provider>
  );
};

export default MessageNotificationContext;
